import * as config from '@/config/env.config';

/**
 * Checks if the user is authenticated according to Auth0 SDK
 * This checks the Auth0 cache in localStorage to determine authentication state
 * @returns {boolean} True if user appears to be authenticated, false otherwise
 */
const isUserAuthenticated = () => {
	if (typeof window === 'undefined') {
		return false;
	}

	try {
		// Auth0 SDK stores cache with keys like @@auth0spajs@@::...
		const auth0CacheKeys = Object.keys(localStorage).filter(key => 
			key.includes('@@auth0spajs@@')
		);

		if (auth0CacheKeys.length === 0) {
			// No Auth0 cache entries - user is not authenticated
			return false;
		}

		// Check if any cache entry contains valid authentication data
		for (const key of auth0CacheKeys) {
			try {
				const cacheValue = localStorage.getItem(key);
				if (cacheValue) {
					const cacheData = JSON.parse(cacheValue);
					// Check if cache contains authentication state
					// Auth0 SDK stores isAuthenticated in the cache body
					if (cacheData?.body?.isAuthenticated === true) {
						return true;
					}
					// Also check for access token or user data as fallback
					if (cacheData?.body?.access_token || cacheData?.body?.user) {
						return true;
					}
				}
			} catch (e) {
				// Skip invalid cache entries
				continue;
			}
		}

		// If we have cache keys but no valid auth data, user is not authenticated
		return false;
	} catch (error) {
		// If we can't check, assume not authenticated to be safe
		console.warn('⚠️  Could not check Auth0 authentication state:', error);
		return false;
	}
};

/**
 * Centralized 401/403 error handler
 * Redirects to /securitycheck for authentication/authorization errors
 * 
 * This is used by:
 * - RTK Query APIs (baseQueryWithAuth)
 * - Legacy middleware functions (get, post, put, callApi, etc.)
 * 
 * Strategy: Only redirect if user is still authenticated (according to Auth0 cache).
 * If user is not authenticated, the 401/403 is expected (e.g., during logout, token expiration).
 * 
 * @param {number|string} status - HTTP status code
 * @param {Object} errorData - Optional error data for logging
 * @param {string} endpoint - Optional endpoint for logging
 */
export const handleAuthError = (status, errorData = null, endpoint = null) => {
	// Only handle 401 (Unauthorized) and 403 (Forbidden)
	if (status !== 401 && status !== 403) {
		return;
	}

	// Don't redirect if already on securitycheck, login, or landing page
	if (typeof window === 'undefined') {
		return;
	}

	const currentPath = window.location.pathname;
	if (currentPath === '/securitycheck' || 
	    currentPath === '/login' || 
	    currentPath === '/') {
		return;
	}

	// Check if user is still authenticated according to Auth0 cache
	// If not authenticated, the 401/403 is expected (don't redirect)
	const isAuthenticated = isUserAuthenticated();
	if (!isAuthenticated) {
		console.log('⚠️  Skipping auth error redirect - user not authenticated (expected)', {
			status,
			endpoint: endpoint || 'unknown',
			reason: 'User is not authenticated, so 401/403 is expected behavior'
		});
		return;
	}

	// User is authenticated but got 401/403 - this is a real problem, redirect
	const errorInfo = {
		status,
		message: errorData?.message || errorData?.error || 'Unauthorized/Forbidden',
		endpoint: endpoint || 'unknown',
		url: window.location.href,
		pathname: currentPath,
		userAuthenticated: true
	};
	console.error('❌ Authentication/Authorization Error:', errorInfo);

	if (config.ENABLE_SECURITY_ALERT) {
		alert('About to redirect to /securitycheck\n\nState:\n- Status: ' + status + '\n- Error: ' + JSON.stringify(errorInfo, null, 2) + '\n\nCheck console and state before clicking OK');
	}
	
	// Redirect to security check page
	window.location.href = '/securitycheck';
};

/**
 * Checks a fetch Response for 401/403 errors and handles them
 * @param {Response} response - Fetch Response object
 * @param {string} endpoint - Optional endpoint for logging
 * @returns {boolean} - True if error was handled (redirect initiated)
 */
export const handleResponseAuthError = async (response, endpoint = null) => {
	if (!response || !response.ok) {
		const status = response?.status;
		if (status === 401 || status === 403) {
			let errorData = null;
			try {
				errorData = await response.json().catch(() => ({}));
			} catch (e) {
				// If JSON parsing fails, use status text
				errorData = { error: response.statusText || 'Unknown error' };
			}
			handleAuthError(status, errorData, endpoint);
			return true; // Error was handled
		}
	}
	return false; // No error or not a 401/403
};
